/**
 * CMS-safe placeholders. WordPress' TinyMCE (and most WYSIWYG editors) drop
 * unknown hyphenated tags such as `<sports-match-center>` on save, while a
 * plain `<div>` with data-* attributes always survives. So an editor can write
 *
 *   <div data-sports-widget="sports-match-center" data-league="1399" data-lang="es"></div>
 *
 * and the bundle upgrades it in place: every `data-<name>` (except the
 * selector itself) becomes an attribute on the real element. Runs once on
 * load and keeps watching the document for placeholders inserted later
 * (page builders, lazy sections). Both spellings work: `sports-match-center`
 * and the short `match-center`.
 */
const SELECTOR = '[data-sports-widget]'

export function upgradePlaceholder(node: Element): void {
  const raw = (node.getAttribute('data-sports-widget') ?? '').trim().toLowerCase()
  if (!raw) return
  const tag = raw.startsWith('sports-') ? raw : `sports-${raw}`
  if (!customElements.get(tag)) return // unknown widget — leave the div alone
  const el = document.createElement(tag)
  for (const { name, value } of Array.from(node.attributes)) {
    if (name === 'data-sports-widget' || !name.startsWith('data-')) continue
    el.setAttribute(name.slice(5), value)
  }
  node.replaceWith(el)
}

export function upgradePlaceholders(root: ParentNode = document): void {
  root.querySelectorAll(SELECTOR).forEach(upgradePlaceholder)
}

export function watchPlaceholders(): void {
  if (typeof document === 'undefined') return
  const run = () => upgradePlaceholders()
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true })
  } else {
    run()
  }
  if (typeof MutationObserver === 'undefined') return
  new MutationObserver((records) => {
    for (const r of records) {
      r.addedNodes.forEach((n) => {
        if (!(n instanceof Element)) return
        if (n.matches(SELECTOR)) upgradePlaceholder(n)
        else upgradePlaceholders(n)
      })
    }
  }).observe(document.documentElement, { childList: true, subtree: true })
}
